import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Loader2, X, GripVertical, Pencil } from "lucide-react";
import { priorityColor } from "@/lib/helpers";

function ScorecardPanel({ canEdit }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", weight: "" });
  const load = useCallback(async () => { const { data } = await api.get("/scorecard-elements"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try { await api.post("/scorecard-elements", { name: form.name, weight: parseFloat(form.weight) || 0 }); setForm({ name: "", weight: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/scorecard-elements/${id}`); load(); } catch (e) {} };
  const total = items.reduce((s, i) => s + (Number(i.weight) || 0), 0);
  return (
    <div className="max-w-xl">
      <h3 className="font-heading text-lg font-semibold tracking-tight">Scorecard Elements</h3>
      <p className="mb-3 text-xs text-zinc-400">Rate potential locations on these. Weights should total 100 (currently {total}).</p>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between border border-zinc-200 bg-white px-3 py-2" data-testid={`scorecard-el-${it.id}`}>
            <span className="text-sm text-zinc-800">{it.name}</span>
            <div className="flex items-center gap-3"><span className="text-xs text-zinc-500">weight {it.weight}</span>
              {canEdit && <button onClick={() => del(it.id)} data-testid={`delete-scorecard-${it.id}`} className="text-zinc-400 hover:text-[#e84e34]"><Trash2 className="h-4 w-4" /></button>}</div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-zinc-400">No elements yet</p>}
      </div>
      {canEdit && (
        <div className="mt-3 flex gap-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Element (e.g. Foot traffic)" data-testid="scorecard-name" className="flex-1 border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
          <input type="number" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} placeholder="Weight" data-testid="scorecard-weight" className="w-24 border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
          <button onClick={add} data-testid="scorecard-add" className="bg-[#e84e34] px-3 text-sm font-medium text-white hover:bg-[#c73e26]"><Plus className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
}

const ROLES = ["admin", "manager", "member"];

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

function PlaybookPanel({ canEdit }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", description: "", category_id: "__none__", priority: "medium" });

  const load = useCallback(async () => {
    const [p, c] = await Promise.all([api.get("/playbook-tasks"), api.get("/task-categories")]);
    setItems(p.data); setCategories(c.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      await api.post("/playbook-tasks", { title: form.title, description: form.description, category_id: form.category_id === "__none__" ? null : form.category_id, priority: form.priority });
      setForm({ title: "", description: "", category_id: "__none__", priority: "medium" });
      load(); toast.success("Playbook task added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/playbook-tasks/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const catName = (id) => categories.find((c) => c.id === id)?.name;

  return (
    <div className="max-w-2xl">
      <h3 className="font-heading text-lg font-semibold tracking-tight">Location Playbook</h3>
      <p className="mb-3 text-xs text-zinc-400">Template tasks auto-created for each new location. You can also apply them to an existing location from the Locations page.</p>
      {loading ? <Loader2 className="mt-4 h-5 w-5 animate-spin text-[#e84e34]" /> : (
        <div className="mt-3 space-y-2">
          {items.map((it) => (
            <div key={it.id} className="flex items-start justify-between border border-zinc-200 bg-white px-3 py-2" data-testid={`playbook-item-${it.id}`}>
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-zinc-800">
                  <span className="h-2 w-2 rounded-full" style={{ background: priorityColor(it.priority) }} />
                  {it.title}
                  {it.category_id && catName(it.category_id) && <span className="border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-500">{catName(it.category_id)}</span>}
                </div>
                {it.description && <p className="mt-0.5 text-xs text-zinc-500">{it.description}</p>}
              </div>
              {canEdit && <button onClick={() => del(it.id)} data-testid={`delete-playbook-${it.id}`} className="text-zinc-400 hover:text-[#e84e34]"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
          {items.length === 0 && <p className="text-sm text-zinc-400">No playbook tasks yet</p>}
        </div>
      )}
      {canEdit && (
        <div className="mt-4 space-y-2 border border-zinc-200 bg-zinc-50 p-3">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Task title" data-testid="playbook-title-input" className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" rows={2} data-testid="playbook-desc-input" className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
              <SelectTrigger data-testid="playbook-category-select" className="rounded-none bg-white"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No category</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="playbook-priority-select" className="rounded-none bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <button onClick={add} data-testid="playbook-add-button" className="flex items-center gap-1 bg-[#e84e34] px-3 py-2 text-sm font-medium text-white hover:bg-[#c73e26]"><Plus className="h-4 w-4" /> Add playbook task</button>
        </div>
      )}
    </div>
  );
}

function ConfigList({ title, endpoint, withColor, canEdit, note, reorderEndpoint, allowRename = false }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#52525b");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [loading, setLoading] = useState(true);
  const dragIdx = useRef(null);

  const load = useCallback(() => api.get(endpoint).then((r) => { setItems(r.data); setLoading(false); }), [endpoint]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) return;
    try {
      await api.post(endpoint, withColor ? { name, color } : { name });
      setName(""); setColor("#52525b"); load();
      toast.success("Added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`${endpoint}/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const rename = async (id) => {
    if (!editName.trim()) return;
    try { await api.patch(`${endpoint}/${id}`, { name: editName }); setEditId(null); load(); toast.success("Renamed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reorderable = canEdit && !!reorderEndpoint;
  const onDrop = async (i) => {
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from == null || from === i) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    setItems(next);
    try { await api.post(reorderEndpoint, { ids: next.map((x) => x.id) }); toast.success("Order updated"); }
    catch (e) { toast.error("Reorder failed"); load(); }
  };

  return (
    <div className="max-w-xl">
      <h3 className="font-heading text-lg font-semibold tracking-tight">{title}</h3>
      {note && <p className="mb-3 text-xs text-zinc-400">{note}</p>}
      {loading ? <Loader2 className="mt-4 h-5 w-5 animate-spin text-[#e84e34]" /> : (
        <div className="mt-3 space-y-2">
          {items.map((it, i) => (
            <div
              key={it.id}
              draggable={reorderable}
              onDragStart={() => (dragIdx.current = i)}
              onDragOver={(e) => reorderable && e.preventDefault()}
              onDrop={() => reorderable && onDrop(i)}
              className={`flex items-center justify-between border border-zinc-200 bg-white px-3 py-2 ${reorderable ? "cursor-grab active:cursor-grabbing" : ""}`}
              data-testid={`config-item-${it.id}`}
            >
              <span className="flex flex-1 items-center gap-2 text-sm text-zinc-800">
                {reorderable && <GripVertical className="h-4 w-4 text-zinc-300" data-testid={`drag-handle-${it.id}`} />}
                {withColor && <span className="h-3 w-3 rounded-full" style={{ background: it.color }} />}
                {allowRename && editId === it.id ? (
                  <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") rename(it.id); if (e.key === "Escape") setEditId(null); }} data-testid={`edit-config-input-${it.id}`} className="flex-1 border border-zinc-200 px-2 py-1 text-sm outline-none focus:border-[#e84e34]" />
                ) : it.name}
              </span>
              {canEdit && (
                <div className="flex items-center gap-1">
                  {allowRename && (editId === it.id ? (
                    <button onClick={() => rename(it.id)} data-testid={`save-config-${it.id}`} className="text-xs font-medium text-[#e84e34] hover:underline">Save</button>
                  ) : (
                    <button onClick={() => { setEditId(it.id); setEditName(it.name); }} data-testid={`edit-config-${it.id}`} className="text-zinc-400 hover:text-[#e84e34]"><Pencil className="h-4 w-4" /></button>
                  ))}
                  <button onClick={() => del(it.id)} data-testid={`delete-config-${it.id}`} className="text-zinc-400 hover:text-[#e84e34]"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
          ))}
          {items.length === 0 && <p className="text-sm text-zinc-400">None yet</p>}
        </div>
      )}
      {canEdit && (
        <div className="mt-3 flex items-center gap-2">
          {withColor && <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-9 w-10 border border-zinc-200 bg-white p-0.5" data-testid="config-color-input" />}
          <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New name…" data-testid="config-name-input" className="flex-1 border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
          <button onClick={add} data-testid="config-add-button" className="flex items-center gap-1 bg-[#e84e34] px-3 py-2 text-sm font-medium text-white hover:bg-[#c73e26]"><Plus className="h-4 w-4" /> Add</button>
        </div>
      )}
    </div>
  );
}

function UsersPanel() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("All fields required");
    setSaving(true);
    try { await api.post("/users", form); setForm({ name: "", email: "", password: "", role: "member" }); setOpen(false); load(); toast.success("User created"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const changeRole = async (id, role) => {
    try { await api.patch(`/users/${id}`, { role }); load(); toast.success("Role updated"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold tracking-tight">Team members</h3>
        <button onClick={() => setOpen(!open)} data-testid="add-user-toggle" className="flex items-center gap-1 bg-[#e84e34] px-3 py-2 text-sm font-medium text-white hover:bg-[#c73e26]"><Plus className="h-4 w-4" /> Add user</button>
      </div>
      {open && (
        <div className="mt-3 grid grid-cols-2 gap-2 border border-zinc-200 bg-zinc-50 p-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" data-testid="new-user-name" className="border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="new-user-email" className="border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
          <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" data-testid="new-user-password" className="border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="new-user-role" className="rounded-none bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <button onClick={create} disabled={saving} data-testid="new-user-submit" className="col-span-2 flex items-center justify-center gap-2 bg-zinc-900 px-3 py-2 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create user
          </button>
        </div>
      )}
      <div className="mt-4 space-y-2">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between border border-zinc-200 bg-white px-3 py-2" data-testid={`user-row-${u.id}`}>
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center bg-zinc-900 text-xs font-bold uppercase text-white">{(u.name || "U").slice(0, 2)}</div>
              <div>
                <div className="text-sm font-medium text-zinc-900">{u.name}</div>
                <div className="text-xs text-zinc-400">{u.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                <SelectTrigger data-testid={`role-select-${u.id}`} className="h-8 w-32 rounded-none text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
              {u.id !== user.id && <button onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`} className="text-zinc-400 hover:text-[#e84e34]"><Trash2 className="h-4 w-4" /></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canConfig = user?.role === "admin" || user?.role === "manager";

  return (
    <div className="p-6">
      <Tabs defaultValue="task-config">
        <TabsList className="rounded-none border border-zinc-200 bg-zinc-50 p-0">
          <TabsTrigger value="task-config" data-testid="tab-task-config" className="rounded-none data-[state=active]:bg-[#e84e34] data-[state=active]:text-white">Tasks</TabsTrigger>
          <TabsTrigger value="product-config" data-testid="tab-product-config" className="rounded-none data-[state=active]:bg-[#e84e34] data-[state=active]:text-white">Products</TabsTrigger>
          <TabsTrigger value="equipment-config" data-testid="tab-equipment-config" className="rounded-none data-[state=active]:bg-[#e84e34] data-[state=active]:text-white">Equipment</TabsTrigger>
          <TabsTrigger value="contact-config" data-testid="tab-contact-config" className="rounded-none data-[state=active]:bg-[#e84e34] data-[state=active]:text-white">Contacts</TabsTrigger>
          <TabsTrigger value="location-config" data-testid="tab-location-config" className="rounded-none data-[state=active]:bg-[#e84e34] data-[state=active]:text-white">Locations</TabsTrigger>
          <TabsTrigger value="playbook" data-testid="tab-playbook" className="rounded-none data-[state=active]:bg-[#e84e34] data-[state=active]:text-white">Location Playbook</TabsTrigger>
          {isAdmin && <TabsTrigger value="users" data-testid="tab-users" className="rounded-none data-[state=active]:bg-[#e84e34] data-[state=active]:text-white">Team</TabsTrigger>}
        </TabsList>

        <TabsContent value="task-config" className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-2">
          <ConfigList title="Task Categories" endpoint="/task-categories" canEdit={isAdmin} note={isAdmin ? "Created by admins only." : "Only admins can edit categories."} />
          <ConfigList title="Task Statuses" endpoint="/task-statuses" reorderEndpoint="/task-statuses/reorder" withColor canEdit={canConfig} note="Drag to reorder how statuses appear on the board and in dropdowns." />
        </TabsContent>

        <TabsContent value="product-config" className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-2">
          <ConfigList title="Product Categories" endpoint="/product-categories" canEdit={canConfig} note="Assign products to these categories." />
          <ConfigList title="Product Statuses" endpoint="/product-statuses" reorderEndpoint="/product-statuses/reorder" withColor canEdit={canConfig} note="Drag to reorder how statuses appear in dropdowns." />
        </TabsContent>

        <TabsContent value="equipment-config" className="mt-6">
          <ConfigList title="Equipment Categories" endpoint="/equipment-categories" canEdit={canConfig} note="Assign equipment to these categories." />
        </TabsContent>

        <TabsContent value="contact-config" className="mt-6">
          <ConfigList title="Contact Categories" endpoint="/supplier-categories" canEdit={canConfig} allowRename note="Group contacts into categories you can add, rename or delete." />
        </TabsContent>

        <TabsContent value="location-config" className="mt-6">
          <div className="space-y-8">
            <ConfigList title="Location Categories" endpoint="/location-categories" canEdit={canConfig} allowRename note="Add, rename or delete categories for locations." />
            <ScorecardPanel canEdit={canConfig} />
          </div>
        </TabsContent>

        <TabsContent value="playbook" className="mt-6">
          <PlaybookPanel canEdit={canConfig} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="users" className="mt-6">
            <UsersPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
